import React from 'react';
import { Box, Typography, Divider } from '@mui/material';

/**
 * @param {{ mark: import('../../types/support').JiraAdfMark; children: React.ReactNode }} props
 */
function ApplyMark({ mark, children }) {
  if (!mark?.type) return children;
  switch (mark.type) {
    case 'strong':
      return <strong>{children}</strong>;
    case 'em':
      return <em>{children}</em>;
    case 'underline':
      return <span style={{ textDecoration: 'underline' }}>{children}</span>;
    case 'code':
      return (
        <code
          style={{
            background: '#f4f4f4',
            borderRadius: 3,
            padding: '1px 4px',
            fontFamily: 'monospace',
            fontSize: '0.88em',
          }}
        >
          {children}
        </code>
      );
    case 'link':
      return (
        <a
          href={String(mark.attrs?.href || '#')}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1565c0' }}
        >
          {children}
        </a>
      );
    case 'textColor':
      return <span style={{ color: String(mark.attrs?.color || 'inherit') }}>{children}</span>;
    default:
      return children;
  }
}

/**
 * @param {{ node: import('../../types/support').JiraAdfNode; inheritColor?: boolean }} props
 */
function RenderNode({ node, inheritColor }) {
  if (!node) return null;

  const { type, content, text, marks, attrs } = node;

  if (type === 'text') {
    let result = <>{text || ''}</>;
    if (marks && marks.length) {
      result = marks.reduceRight(
        (acc, mark) => <ApplyMark mark={mark}>{acc}</ApplyMark>,
        result,
      );
    }
    return result;
  }

  if (type === 'hardBreak') return <br />;
  if (type === 'rule') return <Divider sx={{ my: 1 }} />;

  if (type === 'paragraph') {
    return (
      <Typography
        component="p"
        sx={{
          mb: 0.5,
          whiteSpace: 'pre-wrap',
          color: inheritColor ? 'inherit' : '#333',
          fontSize: '0.9rem',
          lineHeight: 1.6,
        }}
      >
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Typography>
    );
  }

  if (type === 'heading') {
    const level = Number(attrs?.level) || 1;
    const sizeMap = { 1: '1.4rem', 2: '1.25rem', 3: '1.1rem', 4: '1rem', 5: '0.95rem', 6: '0.9rem' };
    return (
      <Typography
        component={`h${level}`}
        sx={{
          fontWeight: 700,
          fontSize: sizeMap[level] || '1rem',
          mb: 0.75,
          color: inheritColor ? 'inherit' : '#222',
        }}
      >
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Typography>
    );
  }

  if (type === 'bulletList') {
    return (
      <Box component="ul" sx={{ pl: 2.5, mb: 0.5 }}>
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Box>
    );
  }

  if (type === 'orderedList') {
    return (
      <Box component="ol" sx={{ pl: 2.5, mb: 0.5 }}>
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Box>
    );
  }

  if (type === 'listItem') {
    return (
      <Typography component="li" sx={{ fontSize: '0.9rem', color: inheritColor ? 'inherit' : '#333', lineHeight: 1.6 }}>
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Typography>
    );
  }

  if (type === 'blockquote') {
    return (
      <Box
        sx={{
          borderLeft: '3px solid #f25d27',
          pl: 1.5,
          my: 0.5,
          color: inheritColor ? 'inherit' : '#555',
          fontStyle: 'italic',
        }}
      >
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Box>
    );
  }

  if (type === 'codeBlock') {
    return (
      <Box
        component="pre"
        sx={{
          background: '#f4f4f4',
          borderRadius: '6px',
          p: 1.5,
          overflowX: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          my: 0.5,
        }}
      >
        <code>
          {(content || []).map((child, i) => (
            <RenderNode key={i} node={child} inheritColor={inheritColor} />
          ))}
        </code>
      </Box>
    );
  }

  if (type === 'doc') {
    return (
      <Box>
        {(content || []).map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </Box>
    );
  }

  // Fallback: tenta renderizar filhos
  if (content && content.length) {
    return (
      <>
        {content.map((child, i) => (
          <RenderNode key={i} node={child} inheritColor={inheritColor} />
        ))}
      </>
    );
  }

  return null;
}

/**
 * Renderiza um documento ADF.
 * @param {{ document?: import('../../types/support').JiraAdfNode; inheritColor?: boolean }} props
 */
export default function AdfRenderer({ document, inheritColor = false }) {
  if (!document) return null;
  return <RenderNode node={document} inheritColor={inheritColor} />;
}
